/**
 * AnnouncementsList — Learning hub "Announcements" tab.
 *
 * - Staff view: read-only list of published announcements they're in
 *   the audience of. Each ack-required item shows an "I've read this"
 *   button until acknowledged.
 * - Manager view: full CRUD (create with audience targeting, edit,
 *   publish-now on scheduled/draft, archive) plus a compliance
 *   drill-down for each item.
 */
import { melLocalToISO, toLocalValue } from "@/lib/intl";
import { useCallback, useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Megaphone, Plus, Loader2, CheckCircle2, Clock, Send, Archive, Edit3, Users as UsersIcon,
} from "lucide-react";


const STATUS_STYLES = {
  draft:     "bg-zinc-100 text-zinc-700 border-zinc-200",
  scheduled: "bg-blue-50 text-blue-800 border-blue-200",
  published: "bg-emerald-50 text-emerald-800 border-emerald-200",
  expired:   "bg-zinc-100 text-zinc-500 border-zinc-200",
};

const ROLE_OPTIONS = ["owner", "manager", "practitioner", "administration", "staff"];


export default function AnnouncementsList() {
  const { user } = useAuth();
  const isManager = user && ["owner", "manager"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(null); // null | 'new' | announcement obj
  const [detail, setDetail] = useState(null);          // announcement being viewed

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rAnn, rPending, rTeams, rUsers] = await Promise.all([
        api.get("/announcements", { params: { include_expired: isManager ? true : false } }),
        api.get("/announcements/pending"),
        isManager ? api.get("/teams") : Promise.resolve({ data: [] }),
        isManager ? api.get("/users") : Promise.resolve({ data: [] }),
      ]);
      setRows(rAnn.data || []);
      setPending(rPending.data || []);
      setTeams(rTeams.data || []);
      setUsers(rUsers.data || []);
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, [isManager]);

  useEffect(() => { load(); }, [load]);

  const pendingIds = useMemo(() => new Set(pending.map((p) => p.id)), [pending]);

  async function acknowledge(ann) {
    try {
      await api.post(`/announcements/${ann.id}/acknowledge`, {});
      toast.success("Acknowledged");
      load();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function publishNow(ann) {
    try {
      await api.post(`/announcements/${ann.id}/publish`, {});
      toast.success("Published — staff notified");
      load();
    } catch (e) {
      toast.error("Publish failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function archive(ann) {
    if (!window.confirm(`Archive "${ann.title}"?`)) return;
    try {
      await api.delete(`/announcements/${ann.id}`);
      toast.success("Archived");
      load();
    } catch (e) {
      toast.error("Archive failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <div className="space-y-4" data-testid="announcements-list">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-zinc-600">
          Broadcast messages that require staff acknowledgement. Target by team,
          role, individual, or everyone.
        </div>
        {isManager && (
          <Button
            onClick={() => setShowEditor("new")}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="announcements-new-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> New announcement
          </Button>
        )}
      </div>

      {pending.length > 0 && (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 flex items-center gap-2 text-sm text-amber-900"
          data-testid="announcements-pending-banner"
        >
          <Megaphone className="h-4 w-4" />
          <span>
            You have <b>{pending.length}</b> announcement{pending.length === 1 ? "" : "s"} awaiting your acknowledgement.
          </span>
        </div>
      )}

      {loading ? (
        <div className="p-6 text-sm text-zinc-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center border border-dashed border-zinc-200 rounded-md text-sm text-zinc-500">
          {isManager
            ? "No announcements yet — click ‘New announcement’ to broadcast."
            : "No announcements for you right now."}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((a) => (
            <AnnouncementRow
              key={a.id}
              ann={a}
              needsAck={pendingIds.has(a.id)}
              isManager={isManager}
              onView={() => setDetail(a)}
              onAcknowledge={() => acknowledge(a)}
              onPublish={() => publishNow(a)}
              onArchive={() => archive(a)}
              onEdit={() => setShowEditor(a)}
            />
          ))}
        </div>
      )}

      {showEditor && (
        <AnnouncementEditor
          ann={showEditor === "new" ? null : showEditor}
          teams={teams}
          users={users}
          onClose={() => setShowEditor(null)}
          onSaved={() => { setShowEditor(null); load(); }}
        />
      )}

      {detail && (
        <AnnouncementDetailDialog
          ann={detail}
          isManager={isManager}
          needsAck={pendingIds.has(detail.id)}
          onClose={() => setDetail(null)}
          onAcknowledge={async () => { await acknowledge(detail); setDetail(null); }}
        />
      )}
    </div>
  );
}


function AnnouncementRow({ ann, needsAck, isManager, onView, onAcknowledge, onPublish, onArchive, onEdit }) {
  const status = ann.status || "published";
  const cls = STATUS_STYLES[status] || STATUS_STYLES.published;
  return (
    <div
      className="border border-zinc-200 rounded-md p-3 hover:border-zinc-400 transition-colors"
      data-testid={`announcement-row-${ann.id}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <button
          onClick={onView}
          className="flex-1 min-w-0 text-left"
          data-testid={`announcement-view-${ann.id}`}
        >
          <div className="text-sm font-medium text-zinc-950 flex items-center gap-2 flex-wrap">
            <Megaphone className="h-4 w-4 text-zinc-500" />
            {ann.title}
            {ann.category && (
              <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 border border-zinc-200">
                {ann.category}
              </span>
            )}
            <span className={`text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border ${cls}`}>
              {status}
            </span>
            {ann.requires_ack && (
              <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-50 text-purple-800 border border-purple-200">
                ack required
              </span>
            )}
            {needsAck && (
              <span
                className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300"
                data-testid={`announcement-needsack-${ann.id}`}
              >
                needs your ack
              </span>
            )}
          </div>
          <div className="text-2xs text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
            {ann.published_at && <span>published {new Date(ann.published_at).toLocaleDateString()}</span>}
            {ann.publish_at && status === "scheduled" && (
              <span>scheduled {new Date(ann.publish_at).toLocaleString()}</span>
            )}
            {ann.expires_at && <span>· expires {new Date(ann.expires_at).toLocaleDateString()}</span>}
            <span>· by {ann.created_by_name}</span>
            <AudienceLabel audience={ann.audience} />
          </div>
        </button>
        <div className="flex items-center gap-1 flex-wrap">
          {needsAck && (
            <Button
              onClick={onAcknowledge}
              size="sm"
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid={`announcement-ack-${ann.id}`}
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> I&apos;ve read this
            </Button>
          )}
          {isManager && (status === "draft" || status === "scheduled") && (
            <Button
              onClick={onPublish}
              size="sm"
              variant="outline"
              className="text-xs"
              data-testid={`announcement-publish-${ann.id}`}
            >
              <Send className="h-3.5 w-3.5 mr-1" /> Publish now
            </Button>
          )}
          {isManager && (
            <>
              <button
                onClick={onEdit}
                className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-md"
                data-testid={`announcement-edit-${ann.id}`}
              >
                <Edit3 className="h-3 w-3" /> Edit
              </button>
              <button
                onClick={onArchive}
                className="text-xs text-red-700 hover:bg-red-50 inline-flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-md"
                data-testid={`announcement-archive-${ann.id}`}
              >
                <Archive className="h-3 w-3" /> Archive
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function AudienceLabel({ audience }) {
  if (!audience) return null;
  const parts = [];
  if (audience.all_staff) parts.push("all staff");
  if (audience.roles?.length) parts.push(`roles: ${audience.roles.join(", ")}`);
  if (audience.team_ids?.length) parts.push(`${audience.team_ids.length} team(s)`);
  if (audience.user_ids?.length) parts.push(`${audience.user_ids.length} user(s)`);
  if (parts.length === 0) return null;
  return <span>· <UsersIcon className="inline h-3 w-3 mr-0.5" />{parts.join(" · ")}</span>;
}


function AnnouncementDetailDialog({ ann, isManager, needsAck, onClose, onAcknowledge }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!isManager) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/announcements/${ann.id}/acknowledgement-status`);
        if (!cancelled) setStatus(r.data);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [ann.id, isManager]);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`announcement-detail-${ann.id}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5" />
            {ann.title}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {ann.published_at && `Published ${new Date(ann.published_at).toLocaleString()} · `}
            by {ann.created_by_name}
          </DialogDescription>
        </DialogHeader>
        <div className="prose prose-sm max-w-none prose-headings:text-zinc-950 prose-p:text-zinc-800">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{ann.body_markdown || "_(no content)_"}</ReactMarkdown>
        </div>
        {isManager && status && (
          <div className="border-t border-zinc-200 pt-3 mt-2" data-testid={`announcement-compliance-${ann.id}`}>
            <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
              <UsersIcon className="h-3 w-3" /> Compliance —{" "}
              <b className="text-zinc-900">{status.acknowledged_count}</b>/<b className="text-zinc-900">{status.total_users}</b> acknowledged
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-2xs text-emerald-800 font-medium mb-1">Acknowledged</div>
                <ul className="text-xs space-y-1">
                  {status.acknowledged.length === 0 && <li className="text-zinc-500 italic">Nobody yet.</li>}
                  {status.acknowledged.map((u) => (
                    <li key={u.user_id} className="text-zinc-800">
                      <CheckCircle2 className="h-3 w-3 inline mr-1 text-emerald-700" /> {u.name}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-2xs text-amber-900 font-medium mb-1">Pending</div>
                <ul className="text-xs space-y-1">
                  {status.pending.length === 0 && <li className="text-zinc-500 italic">Everyone&apos;s up to date. 🎉</li>}
                  {status.pending.map((u) => (
                    <li key={u.user_id} className="text-zinc-800">
                      <span className="inline-block h-2 w-2 rounded-full bg-amber-500 mr-1.5" />
                      {u.name}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          {needsAck && (
            <Button
              onClick={onAcknowledge}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid={`announcement-detail-ack-${ann.id}`}
            >
              <CheckCircle2 className="h-4 w-4 mr-1" /> I&apos;ve read this
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AnnouncementEditor({ ann, teams, users, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    title: ann?.title || "",
    body_markdown: ann?.body_markdown || "",
    category: ann?.category || "",
    audience: {
      all_staff: ann?.audience?.all_staff ?? true,
      team_ids: ann?.audience?.team_ids || [],
      roles: ann?.audience?.roles || [],
      user_ids: ann?.audience?.user_ids || [],
    },
    requires_ack: ann?.requires_ack ?? true,
    publish_at: ann?.publish_at || "",
    expires_at: ann?.expires_at || "",
  }));
  const [saving, setSaving] = useState(false);

  function setAudience(patch) {
    setForm((f) => ({ ...f, audience: { ...f.audience, ...patch } }));
  }

  async function save({ asDraft = false } = {}) {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!(form.audience.all_staff || form.audience.team_ids.length || form.audience.roles.length || form.audience.user_ids.length)) {
      return toast.error("Pick at least one audience filter");
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        body_markdown: form.body_markdown,
        category: form.category.trim() || null,
        audience: form.audience,
        requires_ack: form.requires_ack,
        publish_at: form.publish_at || null,
        expires_at: form.expires_at || null,
      };
      if (ann) {
        await api.patch(`/announcements/${ann.id}`, payload);
      } else {
        await api.post("/announcements", { ...payload, save_as_draft: asDraft });
      }
      toast.success(asDraft ? "Draft saved" : ann ? "Saved" : "Announcement created");
      onSaved?.();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  const staff = useMemo(() => users.filter((u) => !u.disabled), [users]);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="announcement-editor">
        <DialogHeader>
          <DialogTitle>{ann ? "Edit announcement" : "New announcement"}</DialogTitle>
          <DialogDescription className="text-xs">
            Markdown supported in the body. Leave publish date blank to publish
            immediately. Audience defaults to <b>all staff</b>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-2xs">Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Christmas closure dates"
              data-testid="announcement-title"
            />
          </div>
          <div>
            <Label className="text-2xs">Category</Label>
            <Input
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="e.g. Operations, HR, Clinical"
              data-testid="announcement-category"
            />
          </div>
          <div>
            <Label className="text-2xs">Body (markdown)</Label>
            <Textarea
              rows={6}
              value={form.body_markdown}
              onChange={(e) => setForm({ ...form, body_markdown: e.target.value })}
              className="font-mono-num text-sm"
              placeholder="## Details\n\nWrite it here."
              data-testid="announcement-body"
            />
          </div>

          <div className="border border-zinc-200 rounded-md p-3 space-y-3 bg-zinc-50">
            <div className="text-2xs uppercase tracking-wider text-zinc-500 font-medium">Audience</div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.audience.all_staff}
                onChange={(e) => setAudience({ all_staff: e.target.checked })}
                data-testid="announcement-audience-all"
              />
              <span>Everyone (all active staff)</span>
            </label>
            {!form.audience.all_staff && (
              <>
                <div>
                  <Label className="text-2xs">Roles</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {ROLE_OPTIONS.map((r) => {
                      const selected = form.audience.roles.includes(r);
                      return (
                        <button
                          key={r}
                          onClick={() => setAudience({
                            roles: selected
                              ? form.audience.roles.filter((x) => x !== r)
                              : [...form.audience.roles, r],
                          })}
                          className={`text-xs px-2 py-1 rounded-md border ${selected ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300"}`}
                          data-testid={`announcement-audience-role-${r}`}
                        >{r}</button>
                      );
                    })}
                  </div>
                </div>
                {teams.length > 0 && (
                  <div>
                    <Label className="text-2xs">Teams</Label>
                    <select
                      multiple
                      value={form.audience.team_ids}
                      onChange={(e) => setAudience({
                        team_ids: Array.from(e.target.selectedOptions).map((o) => o.value),
                      })}
                      className="mt-1 min-h-[70px] px-2 py-1 border border-zinc-300 rounded-md text-sm bg-white w-full"
                      data-testid="announcement-audience-teams"
                    >
                      {teams.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <Label className="text-2xs">Specific users</Label>
                  <select
                    multiple
                    value={form.audience.user_ids}
                    onChange={(e) => setAudience({
                      user_ids: Array.from(e.target.selectedOptions).map((o) => o.value),
                    })}
                    className="mt-1 min-h-[100px] px-2 py-1 border border-zinc-300 rounded-md text-sm bg-white w-full"
                    data-testid="announcement-audience-users"
                  >
                    {staff.map((u) => (
                      <option key={u.id} value={u.id}>{u.full_name || u.email} · {u.role}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.requires_ack}
              onChange={(e) => setForm({ ...form, requires_ack: e.target.checked })}
              data-testid="announcement-requires-ack"
            />
            <span>Staff must acknowledge this announcement</span>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-2xs">Publish at (optional)</Label>
              <Input
                type="datetime-local"
                value={toLocalValue(form.publish_at)}
                onChange={(e) => setForm({ ...form, publish_at: melLocalToISO(e.target.value) })}
                data-testid="announcement-publish-at"
              />
              <p className="text-2xs text-zinc-500 mt-0.5">Leave blank to publish immediately.</p>
            </div>
            <div>
              <Label className="text-2xs">Expires at (optional)</Label>
              <Input
                type="datetime-local"
                value={toLocalValue(form.expires_at)}
                onChange={(e) => setForm({ ...form, expires_at: melLocalToISO(e.target.value) })}
                data-testid="announcement-expires-at"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {!ann && (
            <Button
              variant="outline"
              onClick={() => save({ asDraft: true })}
              disabled={saving}
              data-testid="announcement-save-draft"
            >
              Save as draft
            </Button>
          )}
          <Button
            onClick={() => save()}
            disabled={saving}
            className="bg-zinc-950 text-white"
            data-testid="announcement-save"
          >
            {saving
              ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</>
              : ann ? "Save" : <><Send className="h-4 w-4 mr-1" /> Publish</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
